import { useCallback, useState } from "react";
import { IconCloseCircleFill, IconFileForbiddenFill, IconPlus } from "@synthesis/shared-icons";
import {
  downloadExternalFinalDecisionFile,
  downloadExternalRequestFile,
  downloadFinalDecisionDocumentFile,
  downloadInternalRequestFile,
} from "@synthesis/shared-services";
import type { FinalDecisionVersionDto } from "@synthesis/shared-services/internal/application/types";
import { Accordion, Button, Modal, setSnack } from "@synthesis/shared-ui";
import { cn, useDownload } from "@synthesis/shared-utils";

import { AttachmentFileRow, type AttachmentFileMeta } from "../../../../components/AttachmentFileRow";
import type { ApplicationStatus } from "../../../../constants";
import { useClientType } from "../../../../shared/hooks/use-client-type";
import {
  isFinalDecisionResolvedStatus,
  type ApplicationDetailDocumentFile,
  type ApplicationDetailFinalDecision,
  type ApplicationDetailFinalDecisionRemark,
} from "../../application-detail-page.lib";
import type { FinalDecisionVersionBundle } from "../../hooks/use-final-decision";
import { DetailFieldRow } from "../detail-field-row";
import { DetailTabPlaceholder } from "../detail-tab-placeholder";
import { formatFinalDecisionDate, shouldCreateFinalDecisionDraft, type FinalDecisionModalMode } from "./final-decision.lib";
import { FinalDecisionModal } from "./final-decision-modal";
import { FinalDecisionVersionAccordion } from "./final-decision-version-accordion";

type FinalDecisionContentProps = {
  requestId: string;
  status?: ApplicationStatus;
  finalDecision: ApplicationDetailFinalDecision;
  showPlaceholder: boolean;
  canAddVersion: boolean;
  canEditDecision: boolean;
  /** Текущая (последняя отправленная) версия итогового решения после отправки на согласование. */
  currentVersion?: FinalDecisionVersionDto | null;
  currentVersionDocuments?: ApplicationDetailDocumentFile[];
  currentVersionRemarks?: ApplicationDetailFinalDecisionRemark[];
  versionHistory?: FinalDecisionVersionBundle[];
  canManageRemarks?: boolean;
  isDraftAccessDenied?: boolean;
  className?: string;
};

const toAttachmentFileMeta = (file: ApplicationDetailDocumentFile): AttachmentFileMeta => ({
  id: file.id,
  baseName: file.baseName,
  ext: file.ext,
});

/** Заголовок карточки: «Итоговое решение от ДД.ММ.ГГГГ» (дата черновика или дата решения). */
const getDecisionCardTitle = (finalDecision: ApplicationDetailFinalDecision, showDate: boolean): string => {
  if (!showDate) {
    return "Итоговое решение";
  }

  const date = finalDecision.decisionDate.split(",")[0]?.trim();

  return date && date !== "—" ? `Итоговое решение от ${date}` : "Итоговое решение";
};

const VERSION_STATUS_BADGE_CLASS = "bg-neutrals-95 subtitle-xs text-type-minor inline-flex shrink-0 items-center rounded-lg px-2 py-0.5";

/**
 * TODO(FINAL-DECISION-DELETE): удаление черновика/версии итогового решения.
 *
 * В макете у карточки черновика есть кнопка «Удалить». Бэкенд пока НЕ предоставляет
 * DELETE /api/internal/requests/{requestId}/final-decision — в контракте у /final-decision
 * только GET/POST/PUT (см. packages/shared/services/specs/internal.json и
 * src/__generated__/internal/application). Единственный существующий delete —
 * files/final-decision-document/delete (удаляет отдельный документ, а не черновик).
 *
 * Поэтому кнопка временно открывает инфо-попап о том, что функция в разработке.
 *
 * Когда endpoint появится и контракт будет перегенерирован (bun run services:generate):
 * 1. В @synthesis/shared-services/internal/application/hooks появится delete-хук
 *    (например, useDeleteFinalDecisionDraft) и его queryKey.
 * 2. Здесь вместо открытия попапа вызвать мутацию и после успеха:
 *    - await invalidateInternalApplicationQueries(queryClient, requestId);
 *      (внутри — resetQueries черновика get2 + invalidate заявки/версий)
 * 3. Обернуть вызов в setPromiseSnack с русскими pending/fulfilled/rejected
 *    (шаблон — FinalDecisionModal.handleSubmit).
 * 4. Удалить этот попап-компонент, его состояние и TODO-комментарии.
 */
type FinalDecisionDeleteNoticeModalProps = {
  onClose: () => void;
};

const FinalDecisionDeleteNoticeModal = ({ onClose }: FinalDecisionDeleteNoticeModalProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Modal.Root isOpen setIsOpen={handleOpenChange}>
      <Modal.Content className="flex max-h-[calc(100vh-4rem)] w-[520px] flex-col overflow-hidden">
        <Modal.Title>Удаление версии</Modal.Title>
        <div className="flex flex-col gap-4 px-8 pb-2 pt-6">
          <h3 className="heading-h4 text-type-common">Удаление итогового решения</h3>
          <p className="paragraph-base text-type-minor">Функция удаления версии итогового решения находится в разработке и будет доступна позже.</p>
        </div>
        <div className="flex shrink-0 gap-4 px-8 pb-6 pt-2">
          <Button type="button" theme="primary" size="lg" onClick={onClose}>
            Понятно
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

export const FinalDecisionContent = ({
  requestId,
  status,
  finalDecision,
  showPlaceholder,
  canAddVersion,
  canEditDecision,
  currentVersion,
  currentVersionDocuments = [],
  currentVersionRemarks = [],
  versionHistory = [],
  canManageRemarks = false,
  isDraftAccessDenied = false,
  className,
}: FinalDecisionContentProps) => {
  const clientType = useClientType();
  const downloadFile = useDownload();
  const [modalMode, setModalMode] = useState<FinalDecisionModalMode | null>(null);
  const [isDeleteNoticeOpen, setIsDeleteNoticeOpen] = useState(false);

  const handleDownload = useCallback(
    async (fileMeta: AttachmentFileMeta, options?: { isFinalDecisionDocument?: boolean }) => {
      try {
        const blob = finalDecision.downloadViaExternalFinalDecision
          ? await downloadExternalFinalDecisionFile({ requestId, fileId: fileMeta.id })
          : finalDecision.isDraft || finalDecision.downloadViaFinalDecisionDocument || options?.isFinalDecisionDocument
            ? await downloadFinalDecisionDocumentFile({ requestId, fileId: fileMeta.id })
            : clientType === "internal"
              ? await downloadInternalRequestFile(fileMeta.id)
              : await downloadExternalRequestFile(fileMeta.id);
        downloadFile(
          {
            id: fileMeta.id,
            baseName: fileMeta.baseName,
            ...(fileMeta.ext ? { ext: fileMeta.ext } : {}),
          },
          blob,
        );
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setSnack({
          title: "Не удалось скачать файл",
          description: message,
          icon: <IconCloseCircleFill className="size-4" />,
        });
      }
    },
    [
      clientType,
      downloadFile,
      finalDecision.downloadViaExternalFinalDecision,
      finalDecision.downloadViaFinalDecisionDocument,
      finalDecision.isDraft,
      requestId,
    ],
  );

  const handleVersionDocumentDownload = useCallback(
    (fileMeta: AttachmentFileMeta) => handleDownload(fileMeta, { isFinalDecisionDocument: true }),
    [handleDownload],
  );

  const closeModal = useCallback(() => setModalMode(null), []);

  const hasDraft = finalDecision.isDraft;
  const showDecisionDate = isFinalDecisionResolvedStatus(status);
  const versionNumber = currentVersion?.versionNumber;
  const hasSubmittedVersion = versionNumber != null;
  const accordionClassName = "[&>div:last-child]:px-4 [&>div:last-child]:pb-3 [&>div:last-child]:pt-1";

  const handleAddVersion = useCallback(() => {
    // POST, если черновика нет (в т.ч. старт доработки после BAC-2324); иначе PUT.
    const shouldCreate = shouldCreateFinalDecisionDraft({
      status,
      hasDraft,
      isDraftAccessDenied,
    });

    setModalMode(shouldCreate ? "create" : "add-version");
  }, [hasDraft, isDraftAccessDenied, status]);

  const renderDecisionAccordion = () => (
    <Accordion
      initialOpen
      title={
        <span className="flex min-w-0 items-center gap-2">
          <span className="subtitle-base text-type-common truncate">{getDecisionCardTitle(finalDecision, hasDraft || showDecisionDate)}</span>
          {hasDraft ? <span className={VERSION_STATUS_BADGE_CLASS}>Решение не отправлено</span> : null}
        </span>
      }
      actions={
        canEditDecision ? (
          <>
            <Button type="button" theme="text" size="xs" onClick={() => setModalMode("edit")}>
              Редактировать
            </Button>
            {/* TODO(FINAL-DECISION-DELETE): заменить попап на реальное удаление черновика, см. FinalDecisionDeleteNoticeModal. */}
            <Button type="button" theme="text" size="xs" onClick={() => setIsDeleteNoticeOpen(true)}>
              Удалить
            </Button>
          </>
        ) : null
      }
      className={accordionClassName}
    >
      <DetailFieldRow label="Результат рассмотрения">{finalDecision.decisionType}</DetailFieldRow>
      {hasDraft ? null : <DetailFieldRow label="Дата принятия решения">{showDecisionDate ? finalDecision.decisionDate : "—"}</DetailFieldRow>}
      {clientType === "external" ? null : <DetailFieldRow label="Автор">{finalDecision.authorName || "—"}</DetailFieldRow>}
      <DetailFieldRow label={clientType === "external" ? "Обоснование решения" : "Причина решения"}>{finalDecision.justification}</DetailFieldRow>
      <DetailFieldRow label="Документы" className="border-b-0">
        {finalDecision.documents.length === 0 ? (
          "—"
        ) : (
          <ul className="flex list-none flex-col gap-1">
            {finalDecision.documents.map((file) => (
              <AttachmentFileRow key={file.id} fileMeta={toAttachmentFileMeta(file)} download={handleDownload} />
            ))}
          </ul>
        )}
      </DetailFieldRow>
    </Accordion>
  );

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {canAddVersion ? (
        <Button
          type="button"
          theme="secondary"
          size="sm"
          className="w-fit"
          leftIcon={<IconPlus className="size-5" aria-hidden />}
          onClick={handleAddVersion}
        >
          Добавить версию
        </Button>
      ) : null}
      {hasSubmittedVersion && currentVersion ? (
        <FinalDecisionVersionAccordion
          requestId={requestId}
          version={currentVersion}
          documents={currentVersionDocuments}
          remarks={currentVersionRemarks}
          initialOpen
          isCurrentVersion
          showDecisionDate={showDecisionDate}
          showAuthor={clientType !== "external"}
          canManageRemarks={canManageRemarks}
          onDownload={handleVersionDocumentDownload}
        />
      ) : null}
      {hasDraft || !showPlaceholder ? renderDecisionAccordion() : null}
      {versionHistory.map(({ version, documents, remarks }) => (
        <FinalDecisionVersionAccordion
          key={version.versionNumber ?? formatFinalDecisionDate(version.submittedAt)}
          requestId={requestId}
          version={version}
          documents={documents}
          remarks={remarks}
          initialOpen={false}
          showDecisionDate={showDecisionDate}
          showAuthor={clientType !== "external"}
          onDownload={handleVersionDocumentDownload}
        />
      ))}
      {showPlaceholder && !hasDraft && !hasSubmittedVersion ? (
        <DetailTabPlaceholder
          icon={<IconFileForbiddenFill className="text-neutrals-80 size-12" aria-hidden />}
          title={clientType === "external" ? "Результат рассмотрения\nещё не сформирован" : "Результаты рассмотрений\nещё не подготовлено"}
          description={
            clientType === "external" ? "После формирования результат рассмотрения появится здесь" : "После загрузки документ появится в этом разделе"
          }
        />
      ) : null}
      {modalMode === "create" || modalMode === "add-version" ? (
        <FinalDecisionModal
          mode={modalMode}
          requestId={requestId}
          initialDecision={modalMode === "add-version" && hasDraft ? finalDecision.decisionValue : undefined}
          initialJustification={modalMode === "add-version" && hasDraft ? finalDecision.justification : undefined}
          existingDocuments={modalMode === "add-version" && hasDraft ? finalDecision.documents : undefined}
          onClose={closeModal}
        />
      ) : null}
      {isDeleteNoticeOpen ? <FinalDecisionDeleteNoticeModal onClose={() => setIsDeleteNoticeOpen(false)} /> : null}
      {modalMode === "edit" ? (
        <FinalDecisionModal
          mode="edit"
          requestId={requestId}
          initialDecision={finalDecision.decisionValue}
          initialJustification={finalDecision.justification}
          existingDocuments={finalDecision.documents}
          onClose={closeModal}
        />
      ) : null}
    </div>
  );
};
